import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CardService } from '../services/cardService';
import { CategoryService } from '../services/categoryService';
import type { Category, SecondaryCategory } from '../types/category';
import { useAuth } from '../context/AuthContext';

const RewardDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reward, setReward] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<any>({});

  // Category states
  const [categories, setCategories] = useState<Category[]>([]);
  const [secondaryCategories, setSecondaryCategories] = useState<SecondaryCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedPrimaryCategory, setSelectedPrimaryCategory] = useState<string>('');

  // Check if user has admin role
  const isAdmin = user?.role === 'admin';

  // Show access denied message for non-admin users
  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-500 mb-4">You need admin privileges to access Rewards management.</p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  useEffect(() => {
    if (id) {
      fetchReward(id);
    }
    fetchCategories();
  }, [id]);

  // Set selected primary category when both reward and categories are loaded
  useEffect(() => {
    if (reward && categories.length > 0 && formData.categoryPrimary) {
      const matchingCategory = categories.find(cat => cat.primaryCategory === formData.categoryPrimary);
      if (matchingCategory) {
        setSelectedPrimaryCategory(matchingCategory.primarySlug);
      }
    }
  }, [reward, categories, formData.categoryPrimary]);

  // Fetch secondary categories when primary category changes
  useEffect(() => {
    if (selectedPrimaryCategory) {
      fetchSecondaryCategories(selectedPrimaryCategory);
    } else {
      setSecondaryCategories([]);
    }
  }, [selectedPrimaryCategory]);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await CategoryService.getPrimaryCategories(true); // Only active categories
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchSecondaryCategories = async (primarySlug: string) => {
    try {
      console.log('Fetching secondary categories for:', primarySlug);
      const response = await CategoryService.getSecondaryCategories(primarySlug, true); // Only active secondary categories
      console.log('Secondary categories response:', response);
      
      // Handle different response structures
      let secondaryData = null;
      if (response.data && Array.isArray(response.data)) {
        secondaryData = response.data;
      } else if (Array.isArray(response)) {
        secondaryData = response;
      } else if (response && typeof response === 'object' && !response.success) {
        // If response is an object but not our expected structure, it might be the data directly
        secondaryData = Array.isArray(response) ? response : [response];
      } else if (response && (response as any).secondaryCategories && Array.isArray((response as any).secondaryCategories)) {
        // Handle case where response has secondaryCategories property
        secondaryData = (response as any).secondaryCategories;
      } else if (response && response.data && (response.data as any).secondaryCategories && Array.isArray((response.data as any).secondaryCategories)) {
        // Handle case where response.data has secondaryCategories property
        secondaryData = (response.data as any).secondaryCategories;
      }
      
      if (secondaryData && Array.isArray(secondaryData)) {
        setSecondaryCategories(secondaryData as unknown as SecondaryCategory[]);
        console.log('Set secondary categories:', secondaryData);
      } else {
        console.log('No secondary categories data or not an array:', response);
        setSecondaryCategories([]);
      }
    } catch (err) {
      console.error('Error fetching secondary categories:', err);
      setSecondaryCategories([]);
    }
  };

  const handlePrimaryCategoryChange = (categorySlug: string) => {
    setSelectedPrimaryCategory(categorySlug);
    const selectedCategory = categories.find(cat => cat.primarySlug === categorySlug);
    if (selectedCategory) {
      handleInputChange('categoryPrimary', selectedCategory.primaryCategory);
    }
    // Reset subcategory when primary category changes
    handleInputChange('subcategory', '');
  };

  const fetchReward = async (rewardId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await CardService.getRewardById(rewardId);
      setReward(response.data);
      setFormData({
        name: response.data?.name || '',
        shortName: response.data?.shortName || '',
        categoryPrimary: response.data?.categoryPrimary || '',
        subcategory: response.data?.subcategory || '',
        rewards: {
          primary: {
            type: response.data?.rewards?.primary?.type || '',
            value: response.data?.rewards?.primary?.value || 0,
            unit: response.data?.rewards?.primary?.unit || '',
            description: response.data?.rewards?.primary?.description || ''
          },
          conditions: {
            minSpend: response.data?.rewards?.conditions?.minSpend ?? null,
            maxSpend: response.data?.rewards?.conditions?.maxSpend ?? null,
            currency: response.data?.rewards?.conditions?.currency || 'INR',
            validFrom: response.data?.rewards?.conditions?.validFrom ?? null,
            validUntil: response.data?.rewards?.conditions?.validUntil ?? null,
            limit: response.data?.rewards?.conditions?.limit ?? null,
            frequency: response.data?.rewards?.conditions?.frequency ?? null
          }
        },
        status: response.data?.status || 'active'
      });
      
      document.title = `${response.data.name} Reward | Rewards Admin`;
    } catch (err) {
      console.error('Error fetching reward:', err);
      setError(err instanceof Error ? err.message : 'Failed to load reward');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => {
      const copy = { ...prev };
      const keys = field.split('.');
      let current: any = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      
      // Auto-set unit based on type selection
      if (field === 'rewards.type') {
        const unitMap: { [key: string]: string } = {
          'points': 'RPs',
          'discount': '%',
          'cashback': '%',
          'voucher': 'INR'
        };
        if (unitMap[value]) {
          copy.rewards.unit = unitMap[value];
        }
      }
      
      return copy;
    });
  };

  const handleSave = async () => {
    if (!id) return;
    try {
      setIsSaving(true);
      setError(null);
      const response = await CardService.updateReward(id, formData);
      setReward(response.data);
      setIsEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating reward:', err);
      setError(err instanceof Error ? err.message : 'Failed to update reward');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!id || !reward) return;
    try {
      setIsToggling(true);
      setError(null);
      const nextStatus = reward.status === 'active' ? 'inactive' : 'active';
      const response = await CardService.updateReward(id, { status: nextStatus });
      setReward(response.data);
      setFormData((prev: any) => ({ ...prev, status: nextStatus }));
    } catch (err) {
      console.error('Error toggling reward status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setIsToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/cards" className="flex items-center text-sm text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </Link>
        </div>
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-500">Loading reward...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>
      </div>
    );
  }

  if (!reward) {
    return null;
  }

  return (
    <div className="space-y-6">
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 text-sm text-green-800">Reward updated successfully.</div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/cards/${reward.cardSlug || reward.bankId?.name}`} className="flex items-center text-sm text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Card
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{reward.name}</h1>
          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
            reward.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
          }`}>
            {reward.status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleStatus}
            disabled={isToggling || isSaving}
            className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border shadow-sm disabled:opacity-50 ${
              reward.status === 'active'
                ? 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                : 'text-white bg-green-600 border-green-600 hover:bg-green-700'
            }`}
            title={reward.status === 'active' ? 'Set to inactive' : 'Set to active'}
          >
            {isToggling ? 'Updating…' : reward.status === 'active' ? 'Deactivate' : 'Activate'}
          </button>

          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Short Name</label>
                <input
                  type="text"
                  value={formData.shortName || ''}
                  onChange={(e) => handleInputChange('shortName', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category Primary</label>
                {isEditing ? (
                  <select
                    value={selectedPrimaryCategory}
                    onChange={(e) => handlePrimaryCategoryChange(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={categoriesLoading}
                  >
                    <option value="">Select a primary category</option>
                    {categories.map((category) => (
                      <option key={category._id} value={category.primarySlug}>
                        {category.primaryCategory}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.categoryPrimary || ''}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                    readOnly
                  />
                )}
                {isEditing && categoriesLoading && (
                  <p className="mt-1 text-xs text-gray-500">Loading categories...</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                {isEditing ? (
                  <select
                    value={formData.subcategory || ''}
                    onChange={(e) => handleInputChange('subcategory', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={!selectedPrimaryCategory || secondaryCategories.length === 0}
                  >
                    <option value="">Select a subcategory</option>
                    {secondaryCategories.map((subcategory) => (
                      <option key={subcategory._id} value={subcategory.name}>
                        {subcategory.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.subcategory || ''}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                    readOnly
                  />
                )}
                {isEditing && selectedPrimaryCategory && secondaryCategories.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">No subcategories available for this category</p>
                )}
                {isEditing && !selectedPrimaryCategory && (
                  <p className="mt-1 text-xs text-gray-500">Please select a primary category first</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Reward Details</h3>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formData.rewards?.type || ''}
                  onChange={(e) => handleInputChange('rewards.type', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={!isEditing}
                >
                  <option value="">Select type</option>
                  <option value="points">Points</option>
                  <option value="discount">Discount</option>
                  <option value="cashback">Cashback</option>
                  <option value="voucher">Voucher</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                <input
                  type="number"
                  value={formData.rewards?.value ?? 0}
                  onChange={(e) => handleInputChange('rewards.value', parseFloat(e.target.value) || 0)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <input
                  type="text"
                  value={formData.rewards?.unit || ''}
                  onChange={(e) => handleInputChange('rewards.unit', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly={!isEditing}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.rewards?.description || ''}
                  onChange={(e) => handleInputChange('rewards.description', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  readOnly={!isEditing}
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Reward Conditions</h3>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Spend</label>
                <input
                  type="number"
                  value={formData.rewards?.minSpend ?? ''}
                  onChange={(e) => {
                    const input = e.target.value;
                    const nextValue = input === '' ? null : Number(input);
                    handleInputChange('rewards.minSpend', Number.isNaN(nextValue as number) ? null : nextValue);
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Spend</label>
                <input
                  type="number"
                  value={formData.rewards?.maxSpend ?? ''}
                  onChange={(e) => {
                    const input = e.target.value;
                    const nextValue = input === '' ? null : Number(input);
                    handleInputChange('rewards.maxSpend', Number.isNaN(nextValue as number) ? null : nextValue);
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <input
                  type="text"
                  value={formData.rewards?.currency || 'INR'}
                  onChange={(e) => handleInputChange('rewards.currency', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
                <input
                  type="number"
                  value={formData.rewards?.limit ?? ''}
                  onChange={(e) => handleInputChange('rewards.limit', parseInt(e.target.value) || null)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <input
                  type="text"
                  value={formData.rewards?.frequency || ''}
                  onChange={(e) => handleInputChange('rewards.frequency', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly={!isEditing}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                {reward.logo?.url ? (
                  <img src={reward.logo.url} alt={reward.logo.alt || reward.name} className="h-12 w-12 object-cover" />
                ) : (
                  <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v16H4z" />
                  </svg>
                )}
              </div>
              <div>
                <div className="text-sm text-gray-500">{reward.bankName} • {reward.cardName}</div>
                <div className="text-sm text-gray-900 font-medium">{reward.categoryPrimary}{reward.subcategory ? ` • ${reward.subcategory}` : ''}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardDetail;
